import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend, ArcElement, PointElement, LineElement } from "chart.js";
import { DateRangePicker } from 'react-date-range';
import { tr } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import {
  getDailyGameTime,
  getGameTimeByPeriod,
  getGamePlayCounts,
  getClothingStats,
  getTopGames,
  getUserFootprints
} from '../services/AnalyticsService';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, ArcElement, PointElement, LineElement);

function Analytics() {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
    endDate: new Date(),
    key: 'selection',
  });

  const ranges = [selectionRange];

  // State for all analytics data
  const [dailyGameTime, setDailyGameTime] = useState(null);
  const [gameTimeByPeriod, setGameTimeByPeriod] = useState(null);
  const [gamePlayCounts, setGamePlayCounts] = useState(null);
  const [clothingStats, setClothingStats] = useState(null);
  const [topGames, setTopGames] = useState([]);
  const [userFootprints, setUserFootprints] = useState(null);

  const fetchFirebaseAnalytics = async () => {
    setLoading(true);
    try {
      const startDate = new Date(selectionRange.startDate);
      const endDate = new Date(selectionRange.endDate);
      endDate.setHours(23, 59, 59, 999);

      const [
        dailyTime,
        gameTime,
        playCounts,
        clothing,
        topGamesData,
        footprints
      ] = await Promise.all([
        getDailyGameTime(startDate, endDate),
        getGameTimeByPeriod(startDate, endDate),
        getGamePlayCounts(startDate, endDate),
        getClothingStats(startDate, endDate),
        getTopGames(startDate, endDate),
        getUserFootprints(startDate, endDate)
      ]);

      setDailyGameTime(dailyTime);
      setGameTimeByPeriod(gameTime);
      setGamePlayCounts(playCounts);
      setClothingStats(clothing);
      setTopGames(topGamesData);
      setUserFootprints(footprints);
    } catch (error) {
      console.error('Firebase analytics fetch error:', error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchFirebaseAnalytics();
  }, [selectionRange]);

  const handleSelect = (ranges) => {
    if (ranges.selection) {
      setSelectionRange(ranges.selection);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${formatNumber(hours)} sa ${minutes}dk`;
    } else if (minutes > 0) {
      return `${formatNumber(minutes)} dk ${formatNumber(secs)}sn`;
    } else {
      return `${formatNumber(secs)} sn`;
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('tr-TR').format(value);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Chart configurations
  const gameTimeChart = () => {
    if (!gameTimeByPeriod?.gameTimes) return null;

    const data = {
      labels: Object.keys(gameTimeByPeriod.gameTimes),
      datasets: [{
        label: 'Geçirilen Toplam Süre (Dakika)',
        data: Object.values(gameTimeByPeriod.gameTimes).map(seconds => Math.round(seconds / 60)),
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      }],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 'bold'
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            label: function(context) {
              const minutes = context.parsed.y;
              const hours = Math.floor(minutes / 60);
              const remainingMinutes = minutes % 60;
              return `${hours > 0 ? hours + 's ' : ''}${remainingMinutes}dk`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            },
            maxRotation: 45
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 11
            },
            callback: function(value) {
              const hours = Math.floor(value / 60);
              const minutes = value % 60;
              return hours > 0 ? `${hours}s ${minutes}dk` : `${minutes}dk`;
            }
          }
        }
      }
    };

    return (
      <div className="h-80 w-full">
        <Bar data={data} options={options} />
      </div>
    );
  };

  const gamePlayCountChart = () => {
    if (!gamePlayCounts?.gameCounts) return null;

    const data = {
      labels: Object.keys(gamePlayCounts.gameCounts),
      datasets: [{
        label: 'Oynanma Sayısı',
        data: Object.values(gamePlayCounts.gameCounts),
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      }],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 'bold'
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            label: function(context) {
              return `${formatNumber(context.parsed.y)} kez oynandı`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            },
            maxRotation: 45
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      }
    };

    return (
      <div className="h-80 w-full">
        <Bar data={data} options={options} />
      </div>
    );
  };

  const topGamesChart = () => {
    if (topGames.length === 0) return null;

    const colors = [
      'rgba(245, 158, 11, 0.1)',   // amber
      'rgba(16, 185, 129, 0.1)',   // emerald
      'rgba(139, 92, 246, 0.1)',   // violet
    ];

    const borderColors = [
      'rgba(245, 158, 11, 1)',
      'rgba(16, 185, 129, 1)',
      'rgba(139, 92, 246, 1)',
    ];

    const data = {
      labels: topGames.map(([game]) => game),
      datasets: [{
        label: 'Oynanma Sayısı',
        data: topGames.map(([, count]) => count),
        backgroundColor: colors,
        borderColor: borderColors,
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      }],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: 'bold'
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(139, 92, 246, 1)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            label: function(context) {
              return `${formatNumber(context.parsed.y)} kez oynandı`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            },
            maxRotation: 45
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      }
    };

    return (
      <div className="h-80 w-full">
        <Bar data={data} options={options} />
      </div>
    );
  };

  const clothingPieChart = () => {
    if (!clothingStats?.favoriteAccessories) return null;

    const topAccessories = clothingStats.favoriteAccessories.slice(0, 5);
    
    const colors = [
      'rgba(239, 68, 68, 0.8)',    // red
      'rgba(59, 130, 246, 0.8)',   // blue
      'rgba(245, 158, 11, 0.8)',   // amber
      'rgba(16, 185, 129, 0.8)',   // emerald
      'rgba(139, 92, 246, 0.8)',   // violet
    ];

    const borderColors = [
      'rgba(239, 68, 68, 1)',
      'rgba(59, 130, 246, 1)',
      'rgba(245, 158, 11, 1)',
      'rgba(16, 185, 129, 1)',
      'rgba(139, 92, 246, 1)',
    ];

    const data = {
      labels: topAccessories.map(([item]) => item),
      datasets: [{
        data: topAccessories.map(([, count]) => count),
        backgroundColor: colors,
        borderColor: borderColors,
        borderWidth: 2,
        hoverOffset: 4,
      }],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'right',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 11,
              weight: '500'
            },
            generateLabels: function(chart) {
              const data = chart.data;
              if (data.labels.length && data.datasets.length) {
                return data.labels.map((label, i) => {
                  const dataset = data.datasets[0];
                  const value = dataset.data[i];
                  const total = dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  
                  return {
                    text: `${label} (${percentage}%)`,
                    fillStyle: dataset.backgroundColor[i],
                    strokeStyle: dataset.borderColor[i],
                    lineWidth: dataset.borderWidth,
                    hidden: false,
                    index: i
                  };
                });
              }
              return [];
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(139, 92, 246, 1)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: false,
          callbacks: {
            label: function(context) {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((context.parsed / total) * 100).toFixed(1);
              return `${context.label}: ${context.parsed} adet (${percentage}%)`;
            }
          }
        }
      }
    };

    return (
      <div className="h-80 w-full">
        <Pie data={data} options={options} />
      </div>
    );
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <div className="text-lg font-medium text-gray-600">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analitik Raporlar</h1>
          <p className="text-gray-600">Detaylı oyun analitikleri ve kullanıcı davranış raporları</p>
        </div>
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="w-full lg:w-auto">
            <div className="bg-white rounded-lg shadow-sm border p-2 overflow-x-auto">
              <DateRangePicker
                ranges={ranges}
                onChange={handleSelect}
                locale={tr}
                showSelectionPreview={true}
                moveRangeOnFirstSelection={false}
                months={1}
                direction="horizontal"
                className="w-full"
                style={{ minWidth: '600px' }}
              />
            </div>
          </div>
          
        </div>
      </div>

          {/* Günlük Oyun Süresi */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-blue-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Oyunda Geçirilen Toplam Süre</h2>
        </div>
        {dailyGameTime && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-800 text-sm uppercase tracking-wide">Toplam Oyun Oynama Süresi</h3>
                  <p className="text-3xl font-bold text-blue-900 mt-2">{formatTime(dailyGameTime.totalTime)}</p>
                </div>
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-xl border border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-emerald-800 text-sm uppercase tracking-wide">Ortalama Oyun Oynama Süresi</h3>
                  <p className="text-3xl font-bold text-emerald-900 mt-2">{formatTime(dailyGameTime.averageSessionTime)}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-violet-50 to-violet-100 p-6 rounded-xl border border-violet-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-violet-800 text-sm uppercase tracking-wide">Toplam Oyun Oynama</h3>
                  <p className="text-3xl font-bold text-violet-900 mt-2">{(dailyGameTime.sessionCount)}</p>
                </div>
                <div className="w-12 h-12 bg-violet-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Oyun Süreleri */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-indigo-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Her Oyunda Geçirilen Süre</h2>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          {gameTimeChart()}
        </div>
      </div>

      {/* Oyun Oynanma Sayıları */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-red-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Her Oyunun Oynanma Sayısı</h2>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          {gamePlayCountChart()}
        </div>
      </div>

      {/* Kıyafet ve Aksesuar İstatistikleri */}
      {/* <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-amber-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Kıyafet ve Aksesuar İstatistikleri</h2>
        </div>
        {clothingStats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl border border-amber-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-amber-800 text-lg">Kıyafet Alma Oranı</h3>
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
              </div>
              <p className="text-4xl font-bold text-amber-900 mb-2">{formatPercentage(clothingStats.clothingRate)}</p>
              <p className="text-sm text-amber-700 font-medium">{clothingStats.clothingCount} / {clothingStats.totalItems} eşya</p>
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-lg mb-4">Favori Aksesuarlar</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                {clothingPieChart()}
              </div>
            </div>
          </div>
        )}
      </div> */}

      {/* Top 3 Oyunlar */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="w-1 h-8 bg-violet-500 rounded-full mr-3"></div>
          <h2 className="text-2xl font-bold text-gray-800">Favori Oyun Sıralaması (Top 3)</h2>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          {topGamesChart()}
        </div>
        {topGames.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topGames.map(([game, count], index) => (
              <div key={game} className={`p-4 rounded-xl border-2 ${
                index === 0 ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300' :
                index === 1 ? 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-300' :
                'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-3 ${
                      index === 0 ? 'bg-yellow-500' :
                      index === 1 ? 'bg-gray-500' :
                      'bg-orange-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">{game}</h3>
                      <p className="text-sm text-gray-600">{formatNumber(count)} kez oynandı</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Analytics;
